import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTag, FaList, FaCog, FaPlus, FaTrash } from 'react-icons/fa';
import styled from 'styled-components';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

const ArrayFieldContainer = styled.div`
  margin-bottom: 20px;
  background: ${props => props.darkMode ? '#2d2d2d' : 'white'};
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,${props => props.darkMode ? '0.2' : '0.04'});
  border: ${props => props.darkMode ? '1px solid #444' : 'none'};
`;

const ArrayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ArrayLabel = styled.label`
  font-weight: 600;
  color: ${props => props.darkMode ? '#f5f5f5' : '#2d3748'};
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: ${props => props.darkMode ? '#f5f5f5' : '#2d3748'};
  }
`;

const ArrayItemContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  background: ${props => props.darkMode ? '#1e1e1e' : '#f8f9fa'};
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${props => props.darkMode ? '#444' : '#e9ecef'};
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const ArrayItemInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.darkMode ? '#555' : '#e2e8f0'};
  border-radius: 6px;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  background: ${props => props.darkMode ? '#333' : 'white'};
  color: ${props => props.darkMode ? '#f5f5f5' : 'inherit'};

  &:focus {
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
    outline: none;
}

  &::placeholder {
    color: ${props => props.darkMode ? '#aaa' : '#a0aec0'};
  }
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  color: ${props => props.danger ? '#e74c3c' : props.color || '#3498db'};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background: ${props => props.danger ? '#fee2e2' : props.hoverBg || '#e2f5fe'};
  }
`;

const AddButton = styled(motion.button)`
  background: ${props => props.darkMode ? '#1a365d' : '#ebf8ff'};
  color: ${props => props.darkMode ? '#90cdf4' : '#3498db'};
  border: 1px dashed ${props => props.darkMode ? '#4299e1' : '#90cdf4'};
  border-radius: 8px;
  padding: 10px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.darkMode ? '#2a4365' : '#bee3f8'};
    border-color: ${props => props.darkMode ? '#63b3ed' : '#3498db'};
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: ${props => props.bg || '#e2f5fe'};
  color: ${props => props.color || '#3498db'};
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Add a handler for preventing form submission on input Enter
const preventEnterSubmit = (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up to the form
    return false; // Extra safety measure for older browsers
  }
};

const renderArrayField = (
  field, 
  label, 
  placeholder,
  chipBg,
  chipColor, 
  iconComponent, 
  tempArrayFields,
  handleArrayItemChange,
  handleAddArrayItem,
  handleRemoveArrayItem,
  handleDragEnd,
  darkMode,
  currentInputValues,
  setCurrentInputValues
) => {
  // Function to add item and clear input
  const handleAddAndClear = () => {
    const inputValue = currentInputValues[field]?.trim();
    if (inputValue) {
      console.log("Adding value to array:", inputValue);
      
      // Add the current input value to the array
      handleAddArrayItem(field, inputValue);
      
      // Clear the input field
      setCurrentInputValues({
        ...currentInputValues,
        [field]: ''
      });
      
      // Debug the tempArrayFields to see if it's updated
      console.log("tempArrayFields after adding:", tempArrayFields[field]);
    }
  };

  // Handle input change
  const handleInputChange = (value) => {
    setCurrentInputValues({
      ...currentInputValues,
      [field]: value
    });
  };

  // Handle key press to add on Enter
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAndClear();
    }
  };

  return (
    <ArrayFieldContainer darkMode={darkMode}>
      <ArrayHeader>
        <ArrayLabel darkMode={darkMode}>
          {iconComponent} {label}
        </ArrayLabel>
      </ArrayHeader>
      
      {/* Single input field for adding new items */}
      <div className="mb-3">
        <div className="d-flex">
          <ArrayItemInput
            type="text"
            value={currentInputValues[field] || ''}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            darkMode={darkMode}
            onKeyDown={handleKeyPress}
            className="flex-grow-1"
          />
          <button
            type="button"
            className="btn btn-primary ms-2"
            onClick={handleAddAndClear}
            disabled={!currentInputValues[field]?.trim()}
          >
            <FaPlus size={14} /> Add
          </button>
        </div>
      </div>
      
      {/* Empty or no items message */}
      {(!tempArrayFields[field] || tempArrayFields[field].length === 0) && (
        <div className={`text-muted py-2 px-3 mb-2 ${darkMode ? 'bg-dark' : 'bg-light'} rounded`}>
          No {label.toLowerCase()} added yet.
        </div>
      )}
      
      {/* Preview section with chips */}
      {tempArrayFields[field]?.length > 0 && (
        <div className="mt-3">
          <h6 className={`${darkMode ? 'text-light' : 'text-muted'} mb-2 small`}>Preview:</h6>
          <ChipsContainer>
            {tempArrayFields[field].map((item, index) => (
              item && (
                <Chip 
                  key={index} 
                  bg={chipBg} 
                  color={chipColor}
                  onClick={() => handleRemoveArrayItem(field, index)}
                  style={{ cursor: 'pointer' }}
                  title="Click to remove"
                >
                  {item} <FaTrash size={10} className="ms-2" />
                </Chip>
              )
            ))}
          </ChipsContainer>
        </div>
      )}
    </ArrayFieldContainer>
  );
};

const FeaturesTab = ({ 
  formData, 
  handleChange,
  tempArrayFields,
  handleArrayItemChange,
  handleAddArrayItem,
  handleRemoveArrayItem,
  handleDragEnd,
  darkMode
}) => {
  // Add state for current input values
  const [currentInputValues, setCurrentInputValues] = useState({
    propertyHighlights: '',
    mainFeatures: '',
    amenities: ''
  });

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {renderArrayField(
        'propertyHighlights', 
        'Property Highlights', 
        'e.g. Swimming Pool, Lake View',
        darkMode ? '#103a52' : '#e2f5fe',
        darkMode ? '#69dbff' : '#3498db',
        <FaTag />,
        tempArrayFields,
        handleArrayItemChange,
        handleAddArrayItem,
        handleRemoveArrayItem,
        handleDragEnd,
        darkMode,
        currentInputValues,
        setCurrentInputValues
      )}
      
      {renderArrayField(
        'mainFeatures', 
        'Main Features', 
        'e.g. Infinity Pool, Private Garden',
        darkMode ? '#1e3215' : '#edfbd2',
        darkMode ? '#8fee3e' : '#4d921e',
        <FaList />,
        tempArrayFields,
        handleArrayItemChange,
        handleAddArrayItem,
        handleRemoveArrayItem,
        handleDragEnd,
        darkMode,
        currentInputValues,
        setCurrentInputValues
      )}
      
      {renderArrayField(
        'amenities', 
        'Amenities', 
        'e.g. Air Conditioning, WiFi, Kitchen',
        darkMode ? '#2a1758' : '#f0e6ff',
        darkMode ? '#c4a6ff' : '#6f42c1',
        <FaCog />,
        tempArrayFields,
        handleArrayItemChange,
        handleAddArrayItem,
        handleRemoveArrayItem,
        handleDragEnd,
        darkMode,
        currentInputValues,
        setCurrentInputValues
      )}
    </motion.div>
  );
};

export default FeaturesTab;
